import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Label } from "@/components/ui/Input";
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/SearchableSelect";
import { HoverHintWithGlyph } from "@/components/ui/title-help";

import { searchApQboExpenseAccounts } from "./apInvoicesApi";

const CATEGORY_HELP =
  "QuickBooks expense account for this charge. It is saved with the line and used when this non-PO invoice is posted as a bill.";

export function ApInvoiceExpenseCategoryField(props: {
  lineKey: string;
  accountId: string | null;
  accountName: string | null;
  disabled: boolean;
  onChange: (account: { id: string; name: string } | null) => void;
  label?: string;
  hoverHint?: string;
}): JSX.Element {
  const [search, setSearch] = useState("");
  const accountsQuery = useQuery({
    queryKey: ["accounting", "ap", "qbo-expense-accounts", props.lineKey, search],
    queryFn: () => searchApQboExpenseAccounts(search),
    enabled: search.trim().length >= 2,
  });
  const options = useMemo((): SearchableSelectOption[] => {
    return (accountsQuery.data ?? []).map((row) => ({
      value: row.id,
      label: row.fullyQualifiedName || row.name,
      description: row.accountType,
    }));
  }, [accountsQuery.data]);

  return (
    <div className="min-w-0">
      <div className="mb-1.5 flex min-h-5 items-center gap-1">
        <Label className="mb-0">{props.label ?? "Category"}</Label>
        <HoverHintWithGlyph hoverHint={props.hoverHint ?? CATEGORY_HELP} />
      </div>
      <SearchableSelect
        className="w-full"
        value={props.accountId ?? ""}
        onValueChange={(option) => {
          if (option == null || option.value === "") {
            props.onChange(null);
            return;
          }
          props.onChange({ id: option.value, name: option.label });
        }}
        options={options}
        searchValue={search}
        onSearchChange={setSearch}
        placeholder="Search categories…"
        searchPlaceholder="Type at least 2 characters…"
        emptyMessage={search.trim().length < 2 ? "Type at least 2 characters to search." : "No categories match."}
        isLoading={accountsQuery.isFetching}
        selectedLabelOverride={props.accountName}
        disabled={props.disabled}
      />
    </div>
  );
}
